import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowLeft, ImageOff, X, ChevronLeft, ChevronRight, Video } from 'lucide-react';

// 1. Tvůj zadaný Google API klíč
const GOOGLE_API_KEY = "AIzaSyBwzMO8NTFSHIGesJ4mRK30-QC3AKPBkh0";

// 2. Tvoje spárovaná ID složek z Google Disku
const FOLDER_IDS = {
  'paleni-carodejnic': '1ujBNxBVp19iD-89SxtZSecBCxdYR2Yyz',
  'detsky-den': '1UVcBiP-pyNSf-_Z94G-1VmZ6459xZLzC',
  'vodo-lodni-soutez': '1I88MzNI1KfEA4-Rnn6LfEuwf2FBiz9xq',
  'kotlikovy-gulas': '1TCgirx1y0g7pyFdzEr1zfGk-qeCbtDgT',
  'ukonceni-prazdnin': '1gfEoIVMw3igpApMGTk12Tcc-I3xQhK0S'
};

const DriveGallery = ({ galleryId, title, videoUrl }) => {
  const [items, setItems] = useState([]); // Přejmenováno z images na items, protože obsahuje i videa
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Stav pro index aktuálně zvětšené fotky/videa v Lightboxu
  const [activeIndex, setActiveIndex] = useState(null);

  const folderId = FOLDER_IDS[galleryId];

  useEffect(() => {
    if (!folderId) {
      setError("Pro tuto galerii nebylo nalezeno ID složky Google Drive.");
      setLoading(false);
      return;
    }

    const fetchItems = async () => {
      try {
        setLoading(true);
        // UPRAVENO: Dotaz nyní hledá 'image/' NEBO 'video/' a v fields požadujeme i 'mimeType'
        const response = await fetch(
          `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+(mimeType+contains+'image/'+or+mimeType+contains+'video/')&key=${GOOGLE_API_KEY}&fields=files(id,name,mimeType)`
        );
        
        if (!response.ok) {
          throw new Error("Google API požadavek selhal. Zkontrolujte API klíč.");
        }

        const data = await response.json();
        if (data.files) {
          setItems(data.files);
        } else {
          setItems([]);
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Při načítání médií došlo k chybě.");
      } finally {
        loading && setLoading(false);
      }
    };

    fetchItems();
  }, [folderId]);

  const nextItem = (e) => {
    e.stopPropagation();
    setActiveIndex((prevIndex) => (prevIndex + 1) % items.length);
  };

  const prevItem = (e) => {
    e.stopPropagation();
    setActiveIndex((prevIndex) => (prevIndex - 1 + items.length) % items.length);
  };

  const closeLightbox = () => {
    setActiveIndex(null);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeIndex === null) return;
      if (e.key === 'ArrowRight') setActiveIndex((prev) => (prev + 1) % items.length);
      if (e.key === 'ArrowLeft') setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
      if (e.key === 'Escape') closeLightbox();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, items]);

  return (
    <div className="py-16 px-4 bg-gray-50 min-h-screen">
      <div className="container mx-auto max-w-6xl">
        
        {/* Tlačítko zpět */}
        <Link 
          to="/fotogalerie" 
          className="inline-flex items-center text-blue-600 font-semibold mb-8 hover:text-blue-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Zpět na přehled galerií
        </Link>

        {/* Nadpis a video sekce */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">{title}</h2>
          
          {/* Tlačítko pro video se ukáže pouze tehdy, pokud je zadáno v props */}
          {videoUrl && (
            <a 
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5"
            >
              <Video className="w-5 h-5" />
              Spustit video z akce
            </a>
          )}
        </div>

        {/* Načítání dat */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
            <p className="text-gray-600 text-lg">Načítám média z Google Disku...</p>
          </div>
        )}

        {/* Zobrazení chyby */}
        {error && (
          <div className="text-center py-16 bg-white rounded-xl shadow-md border border-gray-100 p-8">
            <ImageOff className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <p className="text-red-600 font-medium text-lg mb-2">{error}</p>
          </div>
        )}

        {/* Prázdná složka */}
        {!loading && !error && items.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow-md border border-gray-100 p-8">
            <p className="text-gray-600 text-lg">V této složce zatím nejsou žádné fotografie ani videa.</p>
          </div>
        )}

        {/* Mřížka s náhledy */}
        {!loading && !error && items.length > 0 && (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {items.map((file, index) => {
              const isVideo = file.mimeType && file.mimeType.startsWith('video/');
              
              return (
                <div 
                  key={file.id} 
                  onClick={() => setActiveIndex(index)}
                  className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 transform hover:-translate-y-1 cursor-pointer relative"
                >
                  <div className="block overflow-hidden bg-gray-200 aspect-[4/3] relative">
                    <img 
                      src={`https://drive.google.com/thumbnail?id=${file.id}&sz=w800`} 
                      alt={file.name} 
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    
                    {/* PRIDÁNO: Ikona přehrávače přes náhled, pokud jde o video */}
                    {isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                        <div className="bg-white/90 p-3 rounded-full shadow-lg text-gray-800 transform group-hover:scale-110 transition-transform">
                          <Video className="w-6 h-6 fill-current" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* --- LIGHTBOX MODÁLNÍ OKNO --- */}
        {activeIndex !== null && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm select-none"
            onClick={closeLightbox}
          >
            <button 
              className="absolute top-6 right-6 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all"
              onClick={closeLightbox}
            >
              <X className="w-7 h-7" />
            </button>

            <button 
              className="absolute left-4 md:left-8 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all"
              onClick={prevItem}
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            {/* UPRAVENO: Podmínka pro zobrazení videa nebo obrázku */}
            <div className="w-full max-w-5xl h-[85vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              {items[activeIndex].mimeType && items[activeIndex].mimeType.startsWith('video/') ? (
                /* Pokud je to video, použijeme iframe přehrávač od Google Disku */
                <iframe
                  src={`https://drive.google.com/file/d/${items[activeIndex].id}/preview`}
                  className="w-full h-full max-h-[80vh] rounded shadow-2xl border-0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title={items[activeIndex].name}
                />
              ) : (
                /* Pokud je to obrázek, necháme původní <img> */
                <img 
                  src={`https://drive.google.com/thumbnail?id=${items[activeIndex].id}&sz=w1600`} 
                  alt={items[activeIndex].name} 
                  className="max-w-full max-h-[85vh] object-contain rounded shadow-2xl"
                />
              )}
            </div>

            <button 
              className="absolute right-4 md:right-8 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all"
              onClick={nextItem}
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            <div className="absolute bottom-6 text-white/60 text-sm font-medium">
              {activeIndex + 1} / {items.length}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DriveGallery;
